/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Client List Row
 * Compact, single-line representation of a coach's client for the dashboard list. Designed so a coach
 * with up to ~50 clients can scan them quickly; the full per-client stat grid lives on the client
 * detail page (/coach/client/:id). Shows level, average score and last activity at a glance, surfaces
 * attention states (agreements to sign, inactivity, limited access), and links through to details.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useNavigate } from 'react-router-dom'
import { Target, Lock, FileSignature, ChevronRight, AlertCircle } from 'lucide-react'
import { RELATIONSHIP_STATUS_BADGE as STATUS_BADGE } from '../utils/relationshipBadges'
import { getClientAttention, getClientDisplayName } from '../utils/coachClientList'

// Consistent avatar color from the client id (mirrors the prior ClientProgressCard palette).
const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-yellow-500',
  'bg-red-500',
  'bg-teal-500',
  'bg-orange-500'
]

const formatLastActivity = (date) => {
  if (!date) return 'No activity'
  const activityDate = new Date(date)
  const diffDays = Math.floor((Date.now() - activityDate) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return activityDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const ClientListRow = ({ client, onSignAgreement, inactiveDays }) => {
  const navigate = useNavigate()

  const status = client.relationshipStatus || 'active'
  const isActive = status === 'active'
  const coachSigned = !!client.signedRoles?.includes('coach')
  const coachMustSign = status === 'pending_agreements' && !coachSigned

  const permissions = client.permissions || {
    canViewProgress: true,
    canManageChallenges: true,
    canViewAnalytics: true,
    canSendMessages: true
  }
  const canViewDetails =
    permissions.canViewProgress ||
    permissions.canManageChallenges ||
    permissions.canViewAnalytics ||
    permissions.canSendMessages
  const hasLimitedAccess =
    !permissions.canViewProgress ||
    !permissions.canManageChallenges ||
    !permissions.canViewAnalytics ||
    !permissions.canSendMessages

  const displayName = getClientDisplayName(client)
  const attention = getClientAttention(client, { inactiveDays })
  const statusBadge = !isActive ? STATUS_BADGE[status] : null

  const getInitials = () => {
    const first = client.firstName?.charAt(0) || ''
    const last = client.lastName?.charAt(0) || ''
    return (first + last).toUpperCase() || displayName.charAt(0).toUpperCase() || '?'
  }
  const avatarColor = (() => {
    const hash = (client.id || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
    return AVATAR_COLORS[hash % AVATAR_COLORS.length]
  })()

  const showProgress = isActive && permissions.canViewProgress
  const avgScore = client.stats?.averageScore

  const handleView = () => {
    if (canViewDetails) navigate(`/coach/client/${client.id}`)
  }

  return (
    <div
      onClick={handleView}
      className={`flex items-center gap-3 px-3 py-2.5 ${
        canViewDetails ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''
      } transition-colors`}
    >
      {/* Avatar */}
      <div
        className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}
      >
        {getInitials()}
      </div>

      {/* Name + email */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{displayName}</p>
          {statusBadge && (
            <span
              className={`hidden sm:inline-flex px-1.5 py-0.5 text-[11px] font-medium rounded-full whitespace-nowrap ${statusBadge.cls}`}
            >
              {statusBadge.label}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{client.email}</p>
      </div>

      {/* At-a-glance metrics (active clients with progress permission) */}
      {showProgress ? (
        <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-sm">
          <span className="flex items-center gap-1 text-gray-700 dark:text-gray-300 w-16 justify-end">
            <Target className="w-3.5 h-3.5 text-gray-400" />
            {typeof avgScore === 'number' ? `${Math.round(avgScore * 10) / 10}%` : '—'}
          </span>
          <span
            className={`w-20 text-right text-xs whitespace-nowrap ${
              attention.tier === 2
                ? 'text-amber-600 dark:text-amber-400 font-medium'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {formatLastActivity(client.stats?.lastActivity)}
          </span>
        </div>
      ) : (
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {!isActive && attention.tier <= 1 && (
            <span className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 whitespace-nowrap">
              <AlertCircle className="w-3.5 h-3.5" />
              Agreements pending
            </span>
          )}
          {isActive && !permissions.canViewProgress && (
            <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
              <Lock className="w-3.5 h-3.5" />
              Progress hidden
            </span>
          )}
        </div>
      )}

      {/* Limited-access indicator */}
      {isActive && hasLimitedAccess && (
        <span
          title="The student has limited your access"
          className="hidden md:inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium whitespace-nowrap flex-shrink-0"
        >
          <Lock className="w-3 h-3" />
          Limited
        </span>
      )}

      {/* Action */}
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {coachMustSign ? (
          <button
            onClick={() => onSignAgreement?.(client.relationshipId)}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
          >
            <FileSignature className="w-4 h-4" />
            <span className="hidden sm:inline">Sign agreement</span>
          </button>
        ) : canViewDetails ? (
          <button
            onClick={handleView}
            className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium whitespace-nowrap"
          >
            View
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <span
            title="No permissions granted for this client"
            className="inline-flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 whitespace-nowrap"
          >
            <Lock className="w-4 h-4" />
            No access
          </span>
        )}
      </div>
    </div>
  )
}

export default ClientListRow
